// FP3-035B — Articulation Screener Result Panel.
//
// FP3-035C — Added Norm Comparison section and AI Interpretation
// generator. The norm-lookup snapshot comes from
// `GET /students/{sid}/assessment-runs/{rid}/norm-lookup` and is
// pre-computed deterministically server-side so the AI never sees
// raw norm tables.
import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  XCircle,
  MinusCircle,
  WarningCircle,
  Smiley,
  Sparkle,
  PencilSimple,
  FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// 80% accuracy = clinically "passed" on a screener for a given sound.
// Below 80% is flagged as a concern that warrants follow-up probing.
// A single scored item is enough to render a verdict — many blends
// only have one probe item.
const PASS_THRESHOLD = 80;

const POSITION_LABEL = {
  initial: "Initial",
  medial: "Medial",
  final: "Final",
  blend: "Blend",
};

const ERROR_TYPE_LABEL = {
  substitution: "Substitution",
  omission: "Omission",
  distortion: "Distortion",
  frontal_distortion: "Frontal distortion",
  lateral_distortion: "Lateral distortion",
  other: "Other",
};

// FP3-035B — Omission notation. The clinical convention is a slashed
// zero ("Ø") shown next to the omitted target sound.
function ErrorTag({ item }) {
  if (item.error_type === "omission") {
    return (
      <span
        data-testid={`screener-result-error-omission-${item.id}`}
        className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[#FBE7DC] text-[#7A3A2C] font-mono"
        title="Sound omitted"
      >
        <span className="text-base leading-none">Ø</span> {item.target_sound} omitted
      </span>
    );
  }
  if (item.error_type === "substitution" && item.substitution) {
    return (
      <span
        data-testid={`screener-result-error-substitution-${item.id}`}
        className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[#FBE7DC] text-[#7A3A2C] font-mono"
      >
        {item.substitution} for {item.target_sound}
      </span>
    );
  }
  if (item.error_type) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-[#FBE7DC] text-[#7A3A2C]"
      >
        {ERROR_TYPE_LABEL[item.error_type] || item.error_type}
      </span>
    );
  }
  return null;
}


function SummaryCard({ label, value, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[#ECEAE4] bg-white px-3 py-2"
    >
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">
        {label}
      </p>
      <p
        className={`text-lg font-semibold tabular-nums ${
          accent ? "text-[#1E3F33]" : "text-[#1F2937]"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}


function statusFor(scored, accuracy) {
  // FP3-035B correction — A single scored item IS data: 1/1 = Passed,
  // 0/1 = Concern. Many blends and vocalic-R targets have only one
  // probe item, and reporting "Not enough data" on an administered
  // failing blend is clinically wrong.
  // Only show "Not enough data" when the bucket truly has nothing
  // scored (unvisited or not-attempted only).
  if (scored < 1) {
    return { label: "Not enough data", tone: "grey" };
  }
  if (accuracy >= PASS_THRESHOLD) return { label: "Passed", tone: "green" };
  return { label: "Concern", tone: "red" };
}


function StatusChip({ status, testid }) {
  const palette = {
    green: "bg-[#E8F0EB] text-[#1E3F33] border-[#1E3F33]/20",
    red:   "bg-[#FBE7DC] text-[#7A3A2C] border-[#7A3A2C]/30",
    grey:  "bg-[#F1EFE8] text-[#6B7280] border-[#9B9890]/30",
  }[status.tone];
  const Icon =
    status.tone === "green"
      ? CheckCircle
      : status.tone === "red"
      ? WarningCircle
      : MinusCircle;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${palette}`}
    >
      <Icon size={10} weight="fill" /> {status.label}
    </span>
  );
}


export default function ArticulationScreenerResultPanel({ run, student, onRunUpdated, hideInterpretationActions = false }) {
  const items = run?.items || [];
  const summary = run?.summary || {};
  const studentId = student?.id;
  const runId = run?.id;
  const isCompleted = run?.status === "completed";

  // FP3-035C — Norm lookup is fetched lazily. The hook is intentionally
  // forgiving: print/display routes pass the snapshot pre-stamped on
  // the run.interpretation.norm_lookup_snapshot field; the dialog
  // fetches it fresh from the server on mount.
  const [normLookup, setNormLookup] = useState(
    () => run?.interpretation?.norm_lookup_snapshot || null,
  );
  const [normLoading, setNormLoading] = useState(false);
  const [normError, setNormError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(run?.interpretation?.text || "");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!studentId || !runId || !isCompleted) return;
    // Use cached snapshot when an interpretation is present — that
    // snapshot is the canonical record paired with the saved text.
    if (run?.interpretation?.norm_lookup_snapshot) {
      setNormLookup(run.interpretation.norm_lookup_snapshot);
      return;
    }
    setNormLoading(true);
    setNormError("");
    api
      .getAssessmentRunNormLookup(studentId, runId)
      .then((r) => setNormLookup(r.data))
      .catch((e) => {
        if (e?.response?.status === 400) {
          // Non-screener run — engine doesn't apply. Quiet fail.
          setNormLookup(null);
        } else {
          setNormError("Could not load norm comparison.");
        }
      })
      .finally(() => setNormLoading(false));
  }, [studentId, runId, isCompleted, run?.interpretation?.norm_lookup_snapshot]);

  useEffect(() => {
    setDraftText(run?.interpretation?.text || "");
  }, [run?.interpretation?.text]);

  const handleGenerate = async () => {
    if (!studentId || !runId) return;
    setGenerating(true);
    try {
      const r = await api.generateAssessmentRunInterpretation(studentId, runId);
      if (onRunUpdated) onRunUpdated(r.data);
      toast.success("Interpretation generated · review before sharing");
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not generate interpretation.";
      if (code === "budget_exhausted") {
        toast.error("Universal Key budget is exhausted — top up in Profile → Universal Key.");
      } else if (typeof msg === "string" && (msg.toLowerCase().includes("sex field") || msg.toLowerCase().includes("speech norm category"))) {
        toast.error("Complete the student's Sex field first.");
      } else if (typeof msg === "string") {
        toast.error(msg);
      } else {
        toast.error("Could not generate interpretation.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!studentId || !runId) return;
    setSavingEdit(true);
    try {
      const r = await api.patchAssessmentRunInterpretation(studentId, runId, draftText);
      if (onRunUpdated) onRunUpdated(r.data);
      setEditing(false);
      toast.success("Interpretation updated");
    } catch {
      toast.error("Could not save edit.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Partition items into three result lists ────────────────────
  const { correctItems, incorrectItems, notAttemptedItems } = useMemo(() => {
    const correct = items.filter((i) => i.score === 1);
    const incorrect = items.filter((i) => i.score === 0);
    const notAttempted = items.filter(
      (i) =>
        (i.score === null || i.score === undefined) && i.visited === true,
    );
    return {
      correctItems: correct,
      incorrectItems: incorrect,
      notAttemptedItems: notAttempted,
    };
  }, [items]);

  // ── Per-sound rollup with status ──────────────────────────────
  const bySound = useMemo(() => {
    const src = summary.by_sound || [];
    return src
      .map((b) => ({
        ...b,
        status:
          b.percent_accuracy == null
            ? statusFor(b.scored, 0)
            : statusFor(b.scored, b.percent_accuracy),
      }))
      .sort((a, b) => {
        // Concerns first, then passed, then not enough data — providers
        // scan the table top-to-bottom looking for what to follow up on.
        const rank = { red: 0, green: 1, grey: 2 };
        return rank[a.status.tone] - rank[b.status.tone];
      });
  }, [summary.by_sound]);

  const screenerLabel =
    run?.screener_template_id === "ages_3_5"
      ? "Ages 3-5"
      : run?.screener_template_id === "ages_5_8"
      ? "Ages 5-8"
      : run?.title || "Articulation Screener";

  const ageLabel = run?.student_age_at_assessment?.label || null;

  return (
    <div data-testid="screener-result-panel" className="space-y-5">
      {/* A — Summary cards */}
      <section data-testid="screener-result-summary">
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">
          Result summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          <SummaryCard
            testid="screener-result-accuracy"
            label="Overall accuracy"
            value={
              summary.percent_accuracy == null
                ? "—"
                : `${summary.percent_accuracy}%`
            }
            accent
          />
          <SummaryCard label="Items scored" value={summary.scored_count ?? 0} testid="screener-result-scored" />
          <SummaryCard label="Correct" value={summary.correct_count ?? 0} testid="screener-result-correct-count" />
          <SummaryCard label="Incorrect" value={summary.incorrect_count ?? 0} testid="screener-result-incorrect-count" />
          <SummaryCard
            label="Not attempted"
            value={summary.not_attempted_count ?? notAttemptedItems.length}
            testid="screener-result-not-attempted-count"
          />
          <SummaryCard
            label="Age at assessment"
            value={ageLabel || "—"}
            testid="screener-result-age"
          />
          <SummaryCard
            label="Screener version"
            value={screenerLabel}
            testid="screener-result-version"
          />
        </div>
      </section>

      {/* B — Results by sound */}
      <section data-testid="screener-result-by-sound">
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">
          Results by sound
        </h3>
        {bySound.length === 0 ? (
          <p className="text-xs italic text-[#9B9890]">No sounds scored.</p>
        ) : (
          <div className="rounded-xl border border-[#ECEAE4] bg-white overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6] text-[#6B7280]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Sound</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Correct / Total</th>
                  <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Accuracy</th>
                  <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">Status</th>
                </tr>
              </thead>
              <tbody>
                {bySound.map((b) => (
                  <tr
                    key={b.key}
                    data-testid={`screener-result-sound-${b.key.replace(/[\s/]/g, "_")}`}
                    className="border-t border-[#ECEAE4]"
                  >
                    <td className="px-3 py-2 font-mono text-[#1F2937]">{b.key}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {b.correct}/{b.scored}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#1E3F33]">
                      {b.percent_accuracy == null ? "—" : `${b.percent_accuracy}%`}
                    </td>
                    <td className="px-3 py-2">
                      <StatusChip status={b.status} testid={`screener-result-sound-status-${b.key.replace(/[\s/]/g, "_")}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* C + D + F — Production lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProductionList
          testid="screener-result-correct-list"
          title="Correct productions"
          tone="green"
          items={correctItems}
          icon={CheckCircle}
        />
        <ProductionList
          testid="screener-result-incorrect-list"
          title="Incorrect productions"
          tone="red"
          items={incorrectItems}
          icon={XCircle}
          showErrorDetails
        />
      </div>

      {notAttemptedItems.length > 0 && (
        <ProductionList
          testid="screener-result-not-attempted-list"
          title="Not attempted"
          tone="grey"
          items={notAttemptedItems}
          icon={MinusCircle}
        />
      )}

      {/* FP3-035C — Norm Comparison section */}
      {isCompleted && (
        <NormComparisonSection
          normLookup={normLookup}
          loading={normLoading}
          error={normError}
        />
      )}

      {/* FP3-035C — AI Interpretation section */}
      {isCompleted && !hideInterpretationActions && (
        <InterpretationSection
          run={run}
          normLookup={normLookup}
          generating={generating}
          editing={editing}
          draftText={draftText}
          setDraftText={setDraftText}
          setEditing={setEditing}
          savingEdit={savingEdit}
          onGenerate={handleGenerate}
          onSaveEdit={handleSaveEdit}
        />
      )}

      {/* Print/Display routes pass hideInterpretationActions=true; if
          interpretation exists we still show the read-only block. */}
      {isCompleted && hideInterpretationActions && run?.interpretation?.text && (
        <InterpretationReadOnly run={run} />
      )}

      {/* Provider written sections (if any) */}
      {(run?.clinical_summary || run?.recommendations || run?.provider_notes) && (
        <section className="rounded-xl border border-[#ECEAE4] bg-white p-4 space-y-3">
          {run.clinical_summary && (
            <div data-testid="screener-result-clinical-summary">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Clinical summary</p>
              <p className="text-xs text-[#1F2937] whitespace-pre-wrap">{run.clinical_summary}</p>
            </div>
          )}
          {run.recommendations && (
            <div data-testid="screener-result-recommendations">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Recommendations / next steps</p>
              <p className="text-xs text-[#1F2937] whitespace-pre-wrap">{run.recommendations}</p>
            </div>
          )}
          {run.provider_notes && (
            <div data-testid="screener-result-provider-notes">
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">Provider notes</p>
              <p className="text-xs text-[#1F2937] whitespace-pre-wrap">{run.provider_notes}</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}


function ProductionList({ testid, title, tone, items, icon: Icon, showErrorDetails = false }) {
  const palette = {
    green: { ring: "border-[#D6E4DA]", chip: "bg-[#E8F0EB] text-[#1E3F33]", icon: "text-[#1E3F33]" },
    red:   { ring: "border-[#F1E1D9]", chip: "bg-[#FBE7DC] text-[#7A3A2C]", icon: "text-[#7A3A2C]" },
    grey:  { ring: "border-[#E5E3DB]", chip: "bg-[#F1EFE8] text-[#6B7280]", icon: "text-[#6B7280]" },
  }[tone];
  return (
    <section data-testid={testid} className={`rounded-xl border ${palette.ring} bg-white`}>
      <header className="px-4 py-2 flex items-center justify-between border-b border-[#ECEAE4]">
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#1F2937] inline-flex items-center gap-1.5">
          <Icon size={12} weight="fill" className={palette.icon} /> {title}
        </h3>
        <span className="text-[10px] uppercase tracking-wider text-[#6B7280] tabular-nums">
          {items.length}
        </span>
      </header>
      {items.length === 0 ? (
        <p className="px-4 py-3 text-[11px] italic text-[#9B9890]">None.</p>
      ) : (
        <ul className="divide-y divide-[#ECEAE4]">
          {items.map((it) => (
            <li
              key={it.id || `${it.target_word}-${it.target_sound}`}
              className="px-4 py-2 flex flex-wrap items-center gap-2 text-xs"
            >
              <span className="font-mono text-[#1F2937]">{it.target_sound}</span>
              <span className="text-[#6B7280]">·</span>
              <span className="text-[#1F2937] font-medium">{it.target_word}</span>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${palette.chip}`}>
                {POSITION_LABEL[it.word_position] || it.word_position}
              </span>
              {showErrorDetails && <ErrorTag item={it} />}
              {showErrorDetails && it.notes ? (
                <span className="text-[10px] text-[#6B7280] italic ml-1 truncate" title={it.notes}>
                  — {it.notes}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}



// ── FP3-035C — Norm Comparison + AI Interpretation subcomponents ──


const CATEGORY_META = {
  produced_accurately: {
    label: "Produced accurately",
    tone: "green",
    helper: "Met or exceeded the screener accuracy threshold.",
  },
  beyond_expected: {
    label: "Beyond expected acquisition age",
    tone: "red",
    helper:
      "Incorrect at or above the 90% Iowa-Nebraska criterion — may be an area to review.",
  },
  not_yet_expected: {
    label: "Not yet expected at 90% criterion",
    tone: "amber",
    helper: "Incorrect, but the student is younger than the 90% mastery age.",
  },
  norm_unavailable: {
    label: "Norm lookup unavailable",
    tone: "grey",
    helper: "No matching Iowa-Nebraska row for this item / position.",
  },
  not_attempted: {
    label: "Not attempted",
    tone: "grey",
    helper: "Item was visited but not scored.",
  },
  not_visited: {
    label: "Not administered",
    tone: "grey",
    helper: "Item was not opened during this screening.",
  },
};

const CATEGORY_PALETTE = {
  green: "bg-[#E8F0EB] text-[#1E3F33] border-[#1E3F33]/20",
  red:   "bg-[#FBE7DC] text-[#7A3A2C] border-[#7A3A2C]/30",
  amber: "bg-[#FAF1DD] text-[#7A5B1F] border-[#7A5B1F]/30",
  grey:  "bg-[#F1EFE8] text-[#6B7280] border-[#9B9890]/30",
};

function CategoryChip({ category, testid }) {
  const meta = CATEGORY_META[category] || {
    label: category,
    tone: "grey",
    helper: "",
  };
  return (
    <span
      data-testid={testid}
      title={meta.helper}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${CATEGORY_PALETTE[meta.tone]}`}
    >
      {meta.label}
    </span>
  );
}

// FP3-035C improvement — At-a-glance "priority targets" banner.
//
// Pulls beyond_expected items directly from the deterministic
// snapshot — no extra AI call, no extra round-trip. Surfaces:
//   • a one-line tally of unique (sound, position) pairs that
//     scored incorrectly at or above the 90% mastery age
//   • the specific item chips so a clinician can pattern-match
//     before opening the full table
//   • the special-caution callout for /s/, /z/, /ŋ/ (Iowa-Nebraska
//     flagged irregular acquisition curves)
//
// When zero items land in beyond_expected and no caution sounds
// were touched, we render a calm "no priority targets" line —
// useful confirmation that the screener is clinically clean.
function PriorityTargetsSummary({ priorityTargets, cautions }) {
  const hasPriority = priorityTargets.length > 0;
  const hasCaution = cautions.length > 0;

  if (!hasPriority && !hasCaution) {
    return (
      <div
        className="rounded-md border border-[#D6E4DA] bg-[#E8F0EB] px-3 py-2 text-[11px] text-[#1E3F33]"
        data-testid="screener-norm-priority-targets-empty"
      >
        <strong>No priority targets.</strong> No sounds were incorrect
        at or above the 90% mastery age for this student's age and
        sex.
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-[#E5BBB1] bg-[#FBE7DC] px-3 py-2.5 space-y-1.5"
      data-testid="screener-norm-priority-targets"
    >
      {hasPriority && (
        <>
          <p className="text-[11px] uppercase tracking-wider font-semibold text-[#7A3A2C]">
            Priority targets · {priorityTargets.length}{" "}
            {priorityTargets.length === 1 ? "sound" : "sounds"} beyond
            expected age
          </p>
          <div className="flex flex-wrap gap-1.5">
            {priorityTargets.map((t, i) => (
              <span
                key={`${t.label}-${i}`}
                data-testid={`screener-norm-priority-chip-${i}`}
                title={t.norm_age_label ? `90% mastery: ${t.norm_age_label}` : ""}
                className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded border bg-white text-[#7A3A2C] border-[#7A3A2C]/30"
              >
                {t.label}
                {t.norm_age_label && (
                  <span className="text-[9px] text-[#9B9890] font-sans uppercase tracking-wider">
                    {t.norm_age_label}
                  </span>
                )}
                {t.special_handling && (
                  <span
                    className="text-[9px] text-[#7A5B1F] font-sans uppercase tracking-wider"
                    title="Iowa-Nebraska flagged this sound's acquisition curve as irregular — interpret with clinical judgment."
                  >
                    Caution
                  </span>
                )}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-[#7A3A2C] italic">
            These may be areas to review or consider for intervention
            planning. Informal screener — not a diagnostic
            determination.
          </p>
        </>
      )}
      {hasCaution && (
        <p
          className="text-[10px] text-[#7A5B1F]"
          data-testid="screener-norm-priority-caution-note"
        >
          <strong>Special caution:</strong> {cautions.join(", ")} — the
          90% age reflects strict narrow-transcription standards. Many
          children produce these sounds functionally well before this
          age.
        </p>
      )}
    </div>
  );
}

function NormComparisonSection({ normLookup, loading, error }) {  if (loading) {
    return (
      <section
        className="rounded-xl border border-[#ECEAE4] bg-white p-4"
        data-testid="screener-norm-comparison-loading"
      >
        <p className="text-xs italic text-[#9B9890]">Loading norm comparison…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section
        className="rounded-xl border border-[#F1E1D9] bg-[#FBE7DC] p-3"
        data-testid="screener-norm-comparison-error"
      >
        <p className="text-xs text-[#7A3A2C]">{error}</p>
      </section>
    );
  }
  if (!normLookup) return null;

  const counts = normLookup.counts || {};
  const items = normLookup.items || [];
  const cautions = normLookup.special_caution_sounds || [];
  const missingNorm = normLookup.missing_norm_category;

  // FP3-035C improvement — Priority targets summary.
  // Deterministic derivation from the snapshot (no extra AI call).
  // Each entry is a unique (sound, position) pair, ordered by the
  // first appearance in the run.
  const priorityTargets = [];
  const seenPriority = new Set();
  for (const it of items) {
    if (it.category !== "beyond_expected") continue;
    const key = `${it.target_sound || ""}|${it.word_position || ""}`;
    if (seenPriority.has(key)) continue;
    seenPriority.add(key);
    priorityTargets.push({
      label: `${it.target_sound || "—"} ${it.word_position || ""}`.trim(),
      norm_age_label: it.norm_age_label,
      special_handling: it.norm?.special_handling,
    });
  }

  const tiles = [
    { key: "produced_accurately", label: "Produced accurately", value: counts.produced_accurately || 0, tone: "green" },
    { key: "beyond_expected", label: "Beyond expected age", value: counts.beyond_expected || 0, tone: "red" },
    { key: "not_yet_expected", label: "Not yet expected", value: counts.not_yet_expected || 0, tone: "amber" },
    { key: "norm_unavailable", label: "Norm unavailable", value: counts.norm_unavailable || 0, tone: "grey" },
  ];

  return (
    <section
      data-testid="screener-norm-comparison"
      className="rounded-xl border border-[#ECEAE4] bg-white p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#1F2937]">
            Norm comparison · Iowa-Nebraska (Smit et al., 1990)
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Student age at assessment: <span className="font-mono">{normLookup.student_age_label || "—"}</span>{" "}
            · Sex used for norms:{" "}
            <span className="font-semibold uppercase">
              {normLookup.speech_norm_category || "not set"}
            </span>
          </p>
        </div>
      </header>

      {missingNorm && (
        <div
          className="rounded-md border border-[#E5BBB1] bg-[#FBE7DC] px-3 py-2 text-[11px] text-[#7A3A2C]"
          data-testid="screener-norm-missing-category"
        >
          The student's Sex field is not set (or not supported by the
          female/male norm tables), so norm comparison is not available.
          Complete or confirm Sex on the student's Service Demographics
          tab before scoring against sex-based norms.
        </div>
      )}

      {/* FP3-035C improvement — Priority targets at-a-glance summary.
          Skipped when the sex-based norm lookup is missing (nothing
          to surface). */}
      {!missingNorm && (
        <PriorityTargetsSummary
          priorityTargets={priorityTargets}
          cautions={cautions}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map((t) => (
          <div
            key={t.key}
            data-testid={`screener-norm-tile-${t.key}`}
            className={`rounded-lg border px-3 py-2 ${CATEGORY_PALETTE[t.tone]}`}
          >
            <div className="text-[10px] uppercase tracking-wide font-medium opacity-80">
              {t.label}
            </div>
            <div className="text-xl font-semibold tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>

      {cautions.length > 0 && (
        // Detailed cautions block — kept below the at-a-glance
        // priority chip to give clinicians the full Iowa-Nebraska
        // context when they read past the summary.
        <div
          className="rounded-md border border-[#E5C58E] bg-[#FAF1DD] px-3 py-2 text-[11px] text-[#7A5B1F]"
          data-testid="screener-norm-special-caution"
        >
          <strong>Special caution:</strong>{" "}
          {cautions.join(", ")} — Iowa-Nebraska flagged irregular
          acquisition curves for these sounds. The 90% age reflects strict
          narrow-transcription standards, not functional production.
        </div>
      )}

      <div
        className="overflow-x-auto rounded-md border border-[#ECEAE4]"
        data-testid="screener-norm-table"
      >
        <table className="min-w-full text-xs">
          <thead className="bg-[#F9F8F6] text-[#6B7280] uppercase tracking-wide text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Sound</th>
              <th className="text-left px-3 py-2">Word</th>
              <th className="text-left px-3 py-2">Position</th>
              <th className="text-left px-3 py-2">Result</th>
              <th className="text-left px-3 py-2">Error</th>
              <th className="text-left px-3 py-2">Norm row</th>
              <th className="text-left px-3 py-2">Norm age</th>
              <th className="text-left px-3 py-2">Interpretation</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const norm = it.norm || {};
              const matched = norm.matched;
              const result =
                it.score === 1 || it.score === 1.0
                  ? "Correct"
                  : it.score === 0 || it.score === 0.0
                  ? "Incorrect"
                  : it.visited
                  ? "Not attempted"
                  : "Not administered";
              return (
                <tr
                  key={it.item_id || `${it.target_sound}-${it.target_word}`}
                  data-testid={`screener-norm-row-${it.item_id}`}
                  className="border-t border-[#ECEAE4]"
                >
                  <td className="px-3 py-2 font-mono text-[#1F2937]">{it.target_sound || "—"}</td>
                  <td className="px-3 py-2 text-[#4B5563]">{it.target_word || "—"}</td>
                  <td className="px-3 py-2 text-[#4B5563] capitalize">{it.word_position || "—"}</td>
                  <td className="px-3 py-2 text-[#1F2937]">{result}</td>
                  <td className="px-3 py-2 text-[#7A3A2C]">
                    {it.error_type ? (
                      <span>
                        {it.error_type}
                        {it.substitution ? ` → ${it.substitution}` : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[#4B5563]">
                    {matched ? norm.display_sound : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums text-[#1F2937]">
                    {matched ? norm.age_label : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <CategoryChip
                      category={it.category}
                      testid={`screener-norm-category-${it.item_id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] italic text-[#9B9890]">
        Informal screener interpretation. Iowa-Nebraska 90% criterion ages
        are mastery ages — not the age sounds first emerge. This
        comparison does not replace a full standardized articulation
        assessment.
      </p>
    </section>
  );
}

function InterpretationSection({
  run, normLookup, generating, editing, draftText,
  setDraftText, setEditing, savingEdit, onGenerate, onSaveEdit,
}) {
  const interp = run?.interpretation || null;
  const canGenerate = normLookup && !normLookup.missing_norm_category;

  if (!interp) {
    return (
      <section
        className="rounded-xl border border-[#ECEAE4] bg-white p-4"
        data-testid="screener-interpretation-empty"
      >
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#1F2937]">
              AI interpretation
            </h3>
            <p className="text-[11px] text-[#6B7280] mt-0.5 max-w-xl">
              Generate a calm, norm-grounded narrative based on the
              snapshot above. The AI uses only the deterministic norm
              rows — it cannot invent ages. You can edit the result
              before sharing.
            </p>
          </div>
          <Button
            onClick={onGenerate}
            disabled={generating || !canGenerate}
            data-testid="screener-interpretation-generate"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Sparkle size={14} weight="duotone" className="mr-1" />
            {generating ? "Generating…" : "Generate interpretation"}
          </Button>
        </header>
        {!canGenerate && !generating && (
          <p
            className="text-[11px] text-[#7A3A2C] mt-2"
            data-testid="screener-interpretation-blocked"
          >
            Complete the student's Sex field to enable
            age-based interpretation.
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-[#ECEAE4] bg-white p-4 space-y-3"
      data-testid="screener-interpretation"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#1F2937]">
            AI interpretation
            {interp.provider_edited && (
              <span className="ml-2 text-[10px] text-[#1E3F33] uppercase tracking-wider">
                · Provider edited
              </span>
            )}
          </h3>
          <p className="text-[10px] text-[#9B9890] mt-0.5 italic">
            Generated {interp.generated_at?.slice(0, 10) || ""} using{" "}
            {interp.reference_norm_set_id || "Iowa-Nebraska"} · review
            before sharing
          </p>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="screener-interpretation-edit"
            >
              <PencilSimple size={12} weight="bold" className="mr-1" />
              Edit
            </Button>
          )}
          {!editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerate}
              disabled={generating}
              data-testid="screener-interpretation-regenerate"
            >
              <Sparkle size={12} weight="duotone" className="mr-1" />
              {generating ? "Regenerating…" : "Regenerate"}
            </Button>
          )}
          {editing && (
            <Button
              size="sm"
              onClick={onSaveEdit}
              disabled={savingEdit}
              data-testid="screener-interpretation-save"
              className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              <FloppyDisk size={12} weight="bold" className="mr-1" />
              {savingEdit ? "Saving…" : "Save edit"}
            </Button>
          )}
          {editing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              data-testid="screener-interpretation-cancel"
            >
              Cancel
            </Button>
          )}
        </div>
      </header>

      {editing ? (
        <Textarea
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          data-testid="screener-interpretation-textarea"
          className="min-h-[280px] text-sm leading-relaxed"
        />
      ) : (
        <InterpretationBody interp={interp} testidBase="screener-interpretation" />
      )}
      {!editing && <InterpretationAttribution interp={interp} />}
    </section>
  );
}

function InterpretationReadOnly({ run }) {
  const interp = run?.interpretation;
  if (!interp?.text && !interp?.sections) return null;
  return (
    <section
      className="rounded-xl border border-[#ECEAE4] bg-white p-4 space-y-3"
      data-testid="screener-interpretation-readonly"
    >
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#1F2937]">
        Interpretation{" "}
        {interp.provider_edited ? (
          <span className="text-[10px] text-[#1E3F33] uppercase tracking-wider ml-1">
            · Provider reviewed
          </span>
        ) : (
          <span className="text-[10px] text-[#9B9890] uppercase tracking-wider ml-1">
            · AI generated
          </span>
        )}
      </h3>
      <InterpretationBody interp={interp} testidBase="screener-interpretation-readonly" />
      <InterpretationAttribution interp={interp} />
    </section>
  );
}

// ── FP3-035C correction — Structured interpretation body + attribution ──
//
// Renders the AI-generated `sections` object as polished clinical
// sections. Falls back to plain `text` (already Markdown-scrubbed
// server-side) for legacy interpretations that pre-date the JSON
// pivot. The intent is that the user never sees a `##`, `**`, or
// `---` token — those have been stripped at every layer.

const INTERPRETATION_SECTION_LABELS = {
  overall_summary: "Overall summary",
  strengths: "Sounds produced accurately",
  error_patterns: "Sounds with errors",
  age_based_interpretation: "Age-based interpretation",
  special_cautions: "Special caution notes",
  limitations: "Limitations",
  suggested_next_steps: "Suggested next steps",
};

const INTERPRETATION_SECTION_ORDER = [
  "overall_summary",
  "strengths",
  "error_patterns",
  "age_based_interpretation",
  "special_cautions",
  "limitations",
  "suggested_next_steps",
];

function InterpretationBody({ interp, testidBase = "screener-interpretation" }) {
  const sections = interp?.sections;
  const hasStructured =
    sections &&
    typeof sections === "object" &&
    INTERPRETATION_SECTION_ORDER.some((k) => {
      const v = sections[k];
      return Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim();
    });

  if (hasStructured) {
    return (
      <div
        data-testid={`${testidBase}-sections`}
        className="space-y-4 text-sm text-[#1F2937]"
      >
        {INTERPRETATION_SECTION_ORDER.map((key) => {
          const value = sections[key];
          const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
          if (isEmpty) return null;
          return (
            <div
              key={key}
              data-testid={`${testidBase}-section-${key.replace(/_/g, "-")}`}
            >
              <h4 className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1.5">
                {INTERPRETATION_SECTION_LABELS[key]}
              </h4>
              {key === "overall_summary" ? (
                <p className="leading-relaxed">{value}</p>
              ) : (
                <ul className="space-y-1.5 ml-1">
                  {value.map((b, i) => (
                    <li
                      key={i}
                      className="flex gap-2 leading-relaxed"
                    >
                      <span className="text-[#1E3F33] mt-1.5 flex-shrink-0">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Legacy fallback — Markdown-scrubbed plain text. Wrap each blank
  // line as a paragraph break so it reads cleanly.
  return (
    <div
      data-testid={`${testidBase}-text`}
      className="text-sm text-[#1F2937] whitespace-pre-wrap leading-relaxed"
    >
      {interp?.text || ""}
    </div>
  );
}

function InterpretationAttribution({ interp }) {
  if (!interp) return null;
  const name = interp.generated_by_name || "";
  const roleLabel = interp.generated_by_role_label || interp.generated_by_role || "";
  if (!name && !roleLabel) return null;
  const supervisingName = interp.supervising_provider_name || "";
  const isSLPA = (roleLabel || "").toUpperCase() === "SLPA";
  return (
    <div
      className="border-t border-[#ECEAE4] pt-2 text-[11px] text-[#6B7280] space-y-0.5"
      data-testid="screener-interpretation-attribution"
    >
      <p>
        <span className="font-semibold text-[#1F2937]">Prepared by:</span>{" "}
        {name}
        {roleLabel ? `, ${roleLabel}` : ""}
      </p>
      {isSLPA && (
        <p data-testid="screener-interpretation-supervisor">
          <span className="font-semibold text-[#1F2937]">For review by:</span>{" "}
          {supervisingName ? `${supervisingName}, SLP` : "supervising SLP"}
        </p>
      )}
    </div>
  );
}
